<template>
  <transition name="consultant-modal">
    <div
      v-if="isOpen"
      class="drawer-overlay"
      @click.self="closeDrawer"
    >
      <div class="drawer-box">

        <!-- HEADER -->

        <div class="drawer-header">

          <div class="header-copy">

            <div class="header-icon">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M7 17.5L4.5 19V7.2C4.5 5.7 5.7 4.5 7.2 4.5H16.8C18.3 4.5 19.5 5.7 19.5 7.2V14.3C19.5 15.8 18.3 17 16.8 17H9.2L7 18.5V17.5Z"
                  stroke="currentColor"
                  stroke-width="1.55"
                  stroke-linejoin="round"
                />

                <path
                  d="M8.3 9H15.7"
                  stroke="currentColor"
                  stroke-width="1.55"
                  stroke-linecap="round"
                />

                <path
                  d="M8.3 12.2H13.5"
                  stroke="currentColor"
                  stroke-width="1.55"
                  stroke-linecap="round"
                />
              </svg>
            </div>

            <div>
              <h2 class="title">
                Задать вопрос консультанту
              </h2>

              <p class="description">
                Консультант ответит вам в новом чате
              </p>
            </div>

          </div>


          <button
            type="button"
            class="close-btn"
            aria-label="Закрыть"
            @click="closeDrawer"
          >
            <svg
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M5.5 5.5L14.5 14.5"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linecap="round"
              />

              <path
                d="M14.5 5.5L5.5 14.5"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linecap="round"
              />
            </svg>
          </button>

        </div>


        <!-- CONSULTANT -->

        <div
          v-if="consultantName"
          class="consultant-chip"
        >
          <span class="consultant-dot"></span>

          <span class="consultant-label">
            Вопрос для
          </span>

          <span class="consultant-name">
            {{ consultantName }}
          </span>
        </div>


        <!-- QUESTION -->

        <div class="question-section">

          <div class="field-header">
            <label class="label">
              Ваш вопрос
            </label>

            <span class="field-hint">
              {{ question.length }}/500
            </span>
          </div>


          <div class="question-wrap">

            <textarea
              v-model="question"
              class="question-input"
              maxlength="500"
              placeholder="Например: как лучше подготовиться к собеседованию?"
            ></textarea>

          </div>

        </div>


        <!-- INFO -->

        <div class="info-row">

          <div class="info-icon">
            <svg
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden="true"
            >
              <circle
                cx="10"
                cy="10"
                r="7"
                stroke="currentColor"
                stroke-width="1.4"
              />

              <path
                d="M10 9V13"
                stroke="currentColor"
                stroke-width="1.4"
                stroke-linecap="round"
              />

              <circle
                cx="10"
                cy="6.7"
                r=".8"
                fill="currentColor"
              />
            </svg>
          </div>

          <p>
            После ответа консультанта диалог автоматически появится
            в разделе сообщений.
          </p>

        </div>


        <!-- FOOTER -->

        <div class="drawer-footer">

          <button
            type="button"
            class="cancel-btn"
            :disabled="isSubmitting"
            @click="closeDrawer"
          >
            Отмена
          </button>


          <button
            type="button"
            class="submit-btn"
            :disabled="isSubmitting || !question.trim()"
            @click="sendQuestion"
          >

            <template v-if="!isSubmitting">

              <span>
                Задать вопрос
              </span>

              <svg
                viewBox="0 0 20 20"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M4 10H15.5"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                />

                <path
                  d="M11.5 6L15.5 10L11.5 14"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>

            </template>


            <span
              v-else
              class="loading-content"
            >
              <span class="spinner"></span>

              Отправка
            </span>

          </button>

        </div>

      </div>
    </div>
  </transition>
</template>


<script setup>

import {
  ref
} from "vue";

import ConsultantService
  from "../api/ConsultantService.js";


const props =
  defineProps({

    isOpen: Boolean,

    consultantName: String,

    consultantId: String

  });


const emit =
  defineEmits([
    "close",
    "submitted"
  ]);


const question =
  ref("");


const isSubmitting =
  ref(false);


const closeDrawer = () => {

  if (isSubmitting.value) {
    return;
  }

  emit("close");

};


const sendQuestion =
  async () => {

    const msg =
      question.value.trim();


    if (
      !msg ||
      isSubmitting.value
    ) {
      return;
    }


    isSubmitting.value =
      true;


    try {

      await ConsultantService
        .createChatRequest({

          consultantId:
            props.consultantId,

          message:
            msg

        });


      console.log(
        "✅ Вопрос успешно отправлен"
      );


      emit("submitted");


      question.value =
        "";


      emit("close");

    } catch (error) {

      console.error(
        "❌ Ошибка при отправке вопроса:",
        error
      );


      alert(
        "Ошибка при отправке. Попробуйте ещё раз."
      );

    } finally {

      isSubmitting.value =
        false;

    }

  };

</script>


<style scoped>

/* ============================================================
   OVERLAY
============================================================ */

.drawer-overlay {
  position: fixed;

  inset: 0;

  z-index: 9999;

  display: flex;
  align-items: center;
  justify-content: center;

  box-sizing: border-box;

  padding: 28px;

  background:
    rgba(8, 11, 16, 0.46);

  backdrop-filter:
    blur(8px);

  -webkit-backdrop-filter:
    blur(8px);
}


/* ============================================================
   MODAL
============================================================ */

.drawer-box {
  width: 100%;
  max-width: 560px;

  box-sizing: border-box;

  padding: 26px;

  color:
    #2d3239;

  background:
    rgba(255, 255, 255, 0.985);

  border:
    1px solid
    rgba(35, 50, 70, 0.07);

  border-radius:
    24px;

  box-shadow:
    0
    28px
    80px
    rgba(0, 0, 0, 0.20);

  font-family:
    "Eastman-Regular",
    sans-serif;
}


/* ============================================================
   HEADER
============================================================ */

.drawer-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;

  gap: 18px;
}


.header-copy {
  min-width: 0;

  display: flex;
  align-items: center;

  gap: 12px;
}


.header-icon {
  width: 42px;
  height: 42px;

  flex:
    0
    0
    42px;

  display: flex;
  align-items: center;
  justify-content: center;

  color:
    #557fb5;

  background:
    rgba(70, 120, 190, 0.08);

  border:
    1px solid
    rgba(70, 120, 190, 0.08);

  border-radius:
    12px;
}


.header-icon svg {
  width: 20px;
  height: 20px;
}


.title {
  margin: 0;

  color:
    #292e35;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size:
    17px;

  line-height:
    1.2;

  letter-spacing:
    -0.4px;
}


.description {
  margin:
    4px
    0
    0;

  color:
    #8a9199;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size:
    10px;

  line-height:
    1.4;
}


/* ============================================================
   CLOSE
============================================================ */

.close-btn {
  width: 32px;
  height: 32px;

  flex:
    0
    0
    32px;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 0;

  color:
    #78808a;

  background:
    #f3f5f7;

  border:
    1px solid
    rgba(40, 55, 75, 0.055);

  border-radius:
    10px;

  cursor: pointer;

  transition:
    color .18s ease,
    background-color .18s ease,
    transform .18s ease;
}


.close-btn svg {
  width: 15px;
  height: 15px;
}


.close-btn:hover {
  color:
    #414850;

  background:
    #eaedf1;

  transform:
    scale(1.04);
}


/* ============================================================
   CONSULTANT CHIP
============================================================ */

.consultant-chip {
  width: fit-content;

  margin-top:
    20px;

  display: flex;
  align-items: center;

  gap: 6px;

  padding:
    7px
    10px;

  color:
    #7b838d;

  background:
    #f5f7f9;

  border:
    1px solid
    rgba(35, 50, 70, 0.055);

  border-radius:
    10px;

  font-size:
    9px;
}


.consultant-dot {
  width: 6px;
  height: 6px;

  flex:
    0
    0
    6px;

  background:
    #4d9b78;

  border-radius:
    50%;

  box-shadow:
    0
    0
    0
    3px
    rgba(77, 155, 120, 0.08);
}


.consultant-label {
  color:
    #969ca4;
}


.consultant-name {
  color:
    #5e6874;

  font-family:
    "Eastman-Medium",
    sans-serif;
}


/* ============================================================
   QUESTION
============================================================ */

.question-section {
  margin-top:
    20px;
}


.field-header {
  display: flex;
  align-items: center;
  justify-content: space-between;

  margin-bottom:
    8px;
}


.label {
  color:
    #555d67;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    11px;
}


.field-hint {
  color:
    #a0a6ae;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size:
    9px;
}


.question-wrap {
  overflow: hidden;

  background:
    #f6f7f9;

  border:
    1px solid
    rgba(35, 50, 70, 0.07);

  border-radius:
    15px;

  transition:
    background-color .18s ease,
    border-color .18s ease,
    box-shadow .18s ease;
}


.question-wrap:focus-within {
  background:
    #ffffff;

  border-color:
    rgba(55, 127, 228, 0.38);

  box-shadow:
    0
    0
    0
    4px
    rgba(55, 127, 228, 0.055);
}


.question-input {
  width: 100%;
  height: 125px;

  display: block;

  box-sizing: border-box;

  padding:
    13px
    14px;

  resize: none;

  color:
    #363c44;

  background:
    transparent;

  border:
    none;

  outline:
    none;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size:
    12px;

  line-height:
    1.5;
}


.question-input::placeholder {
  color:
    #a3a9b1;
}


/* ============================================================
   INFO
============================================================ */

.info-row {
  margin-top:
    12px;

  display: flex;
  align-items: flex-start;

  gap: 8px;

  color:
    #8e959e;
}


.info-icon {
  width: 19px;
  height: 19px;

  flex:
    0
    0
    19px;

  display: flex;
  align-items: center;
  justify-content: center;

  color:
    #8399b5;
}


.info-icon svg {
  width: 15px;
  height: 15px;
}


.info-row p {
  margin: 0;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size:
    9px;

  line-height:
    1.45;
}


/* ============================================================
   FOOTER
============================================================ */

.drawer-footer {
  margin-top:
    20px;

  padding-top:
    18px;

  display: flex;
  justify-content: flex-end;

  gap: 9px;

  border-top:
    1px solid
    rgba(35, 50, 70, 0.06);
}


.cancel-btn,
.submit-btn {
  height:
    40px;

  box-sizing:
    border-box;

  padding:
    0
    17px;

  border-radius:
    11px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    11px;

  cursor:
    pointer;

  transition:
    transform .18s ease,
    background-color .18s ease,
    color .18s ease,
    border-color .18s ease,
    box-shadow .18s ease,
    opacity .18s ease;
}


.cancel-btn {
  color:
    #737b84;

  background:
    transparent;

  border:
    1px solid
    rgba(35, 50, 70, 0.08);
}


.cancel-btn:hover {
  color:
    #434a52;

  background:
    #f2f4f6;
}


.submit-btn {
  min-width:
    145px;

  display:
    flex;
  align-items:
    center;
  justify-content:
    center;

  gap:
    7px;

  color:
    #ffffff;

  background:
    linear-gradient(
      135deg,
      #3e84dc,
      #356aaa
    );

  border:
    1px solid
    rgba(50, 105, 180, 0.13);

  box-shadow:
    0
    8px
    18px
    rgba(40, 95, 170, 0.16);
}


.submit-btn svg {
  width:
    14px;

  height:
    14px;
}


.submit-btn:not(:disabled):hover {
  transform:
    translateY(-1px);

  box-shadow:
    0
    11px
    22px
    rgba(40, 95, 170, 0.22);
}


.submit-btn:disabled,
.cancel-btn:disabled {
  opacity:
    .45;

  cursor:
    default;

  pointer-events:
    none;
}


/* ============================================================
   LOADING
============================================================ */

.loading-content {
  display:
    inline-flex;

  align-items:
    center;

  gap:
    7px;
}


.spinner {
  width:
    13px;

  height:
    13px;

  box-sizing:
    border-box;

  border:
    2px solid
    rgba(255, 255, 255, .30);

  border-top-color:
    #ffffff;

  border-radius:
    50%;

  animation:
    spin
    .65s
    linear
    infinite;
}


@keyframes spin {

  to {
    transform:
      rotate(360deg);
  }

}


/* ============================================================
   DARK
============================================================ */

html[data-theme="dark"]
.drawer-overlay {
  background:
    rgba(0, 0, 0, .57);
}


html[data-theme="dark"]
.drawer-box {
  color:
    #eceef1;

  background:
    linear-gradient(
      145deg,
      #22242a,
      #1d1f24
    );

  border:
    1px solid
    rgba(255, 255, 255, .065);

  box-shadow:
    0
    30px
    90px
    rgba(0, 0, 0, .46);
}


html[data-theme="dark"]
.header-icon {
  color:
    #91b6ee;

  background:
    rgba(90, 135, 195, .09);

  border-color:
    rgba(115, 155, 210, .08);
}


html[data-theme="dark"]
.title {
  color:
    #f0f1f3;
}


html[data-theme="dark"]
.description {
  color:
    #858a93;
}


html[data-theme="dark"]
.close-btn {
  color:
    #9ea4ad;

  background:
    rgba(255, 255, 255, .04);

  border-color:
    rgba(255, 255, 255, .055);
}


html[data-theme="dark"]
.close-btn:hover {
  color:
    #e3e5e8;

  background:
    rgba(255, 255, 255, .065);
}


html[data-theme="dark"]
.consultant-chip {
  color:
    #858c95;

  background:
    rgba(255, 255, 255, .025);

  border-color:
    rgba(255, 255, 255, .055);
}


html[data-theme="dark"]
.consultant-label {
  color:
    #70767e;
}


html[data-theme="dark"]
.consultant-name {
  color:
    #b9c2ce;
}


html[data-theme="dark"]
.label {
  color:
    #c5c9cf;
}


html[data-theme="dark"]
.field-hint {
  color:
    #6f747c;
}


html[data-theme="dark"]
.question-wrap {
  background:
    rgba(255, 255, 255, .025);

  border-color:
    rgba(255, 255, 255, .065);
}


html[data-theme="dark"]
.question-wrap:focus-within {
  background:
    rgba(255, 255, 255, .035);

  border-color:
    rgba(91, 158, 255, .34);

  box-shadow:
    0
    0
    0
    4px
    rgba(91, 158, 255, .05);
}


html[data-theme="dark"]
.question-input {
  color:
    #dce0e5;
}


html[data-theme="dark"]
.question-input::placeholder {
  color:
    #60666f;
}


html[data-theme="dark"]
.info-row {
  color:
    #747b84;
}


html[data-theme="dark"]
.info-icon {
  color:
    #8299b8;
}


html[data-theme="dark"]
.drawer-footer {
  border-color:
    rgba(255, 255, 255, .055);
}


html[data-theme="dark"]
.cancel-btn {
  color:
    #969ca5;

  border-color:
    rgba(255, 255, 255, .065);
}


html[data-theme="dark"]
.cancel-btn:hover {
  color:
    #d5d8dc;

  background:
    rgba(255, 255, 255, .04);
}


html[data-theme="dark"]
.submit-btn {
  background:
    linear-gradient(
      135deg,
      #3b6ea8,
      #304f78
    );

  border-color:
    rgba(255, 255, 255, .065);

  box-shadow:
    0
    9px
    22px
    rgba(0, 0, 0, .20);
}


/* ============================================================
   TRANSITION
============================================================ */

.consultant-modal-enter-active,
.consultant-modal-leave-active {
  transition:
    opacity
    .22s
    ease;
}


.consultant-modal-enter-active
.drawer-box,
.consultant-modal-leave-active
.drawer-box {
  transition:
    transform
    .28s
    cubic-bezier(.22, .61, .36, 1),
    opacity
    .22s
    ease;
}


.consultant-modal-enter-from,
.consultant-modal-leave-to {
  opacity:
    0;
}


.consultant-modal-enter-from
.drawer-box,
.consultant-modal-leave-to
.drawer-box {
  transform:
    translateY(16px)
    scale(.985);

  opacity:
    0;
}


/* ============================================================
   MOBILE
============================================================ */

@media (max-width: 600px) {

  .drawer-overlay {
    align-items:
      flex-end;

    padding:
      0;
  }


  .drawer-box {
    max-width:
      none;

    padding:
      20px
      16px
      calc(
        18px +
        env(safe-area-inset-bottom)
      );

    border-radius:
      22px
      22px
      0
      0;
  }


  .header-icon {
    display:
      none;
  }


  .title {
    font-size:
      16px;
  }


  .cancel-btn {
    display:
      none;
  }


  .submit-btn {
    width:
      100%;
  }

}


/* ============================================================
   REDUCED MOTION
============================================================ */

@media (prefers-reduced-motion: reduce) {

  .consultant-modal-enter-active,
  .consultant-modal-leave-active,
  .consultant-modal-enter-active .drawer-box,
  .consultant-modal-leave-active .drawer-box,
  .close-btn,
  .question-wrap,
  .submit-btn,
  .cancel-btn {
    transition:
      none;
  }

}

</style>